import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import type { DismantlingMethodMaster } from './types';
import { useCreateDismantlingMethod, useUpdateDismantlingMethod } from './useDismantlingMethods';

const NS = 'masters';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

const buildSchema = (t: TFn, isEdit: boolean) =>
  z.object({
    method_code: isEdit
      ? z.string()
      : z
          .string()
          .min(1, t('efs.masters.dismantling_methods.validation.code_required', { ns: NS }))
          .max(64)
          .regex(
            /^[a-zA-Z0-9_-]+$/,
            t('efs.masters.dismantling_methods.validation.code_format', { ns: NS }),
          ),
    display_name: z
      .string()
      .min(1, t('efs.masters.dismantling_methods.validation.name_required', { ns: NS }))
      .max(200),
    description: z.string().max(2000).optional(),
  });

type FormInput = z.infer<ReturnType<typeof buildSchema>>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When supplied the dialog edits that row instead of creating one. */
  row?: DismantlingMethodMaster | null;
}

export const DismantlingMethodsFormDialog = ({ open, onOpenChange, row }: Props) => {
  const { t } = useTranslation();
  const create = useCreateDismantlingMethod();
  const update = useUpdateDismantlingMethod();
  const isEdit = !!row;

  const schema = useMemo(() => buildSchema(t, isEdit), [t, isEdit]);
  const form = useForm<FormInput>({
    resolver: zodResolver(schema),
    defaultValues: { method_code: '', display_name: '', description: '' },
  });

  useEffect(() => {
    if (!open) {
      form.reset({ method_code: '', display_name: '', description: '' });
      create.reset();
      update.reset();
      return;
    }
    if (row) {
      form.reset({
        method_code: row.method_code,
        display_name: row.display_name,
        description: row.description ?? '',
      });
    }
    // Mutation resets are deliberately not dependencies: including them would
    // re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    const description = values.description?.trim() ? values.description.trim() : undefined;
    if (isEdit && row) {
      await update.mutateAsync({
        id: row.dismantling_method_id,
        body: { display_name: values.display_name.trim(), description },
      });
    } else {
      await create.mutateAsync({
        method_code: values.method_code.trim(),
        display_name: values.display_name.trim(),
        description,
      });
    }
    onOpenChange(false);
  });

  const isPending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {isEdit
              ? t('efs.masters.dismantling_methods.edit_title', { ns: NS })
              : t('efs.masters.dismantling_methods.create_title', { ns: NS })}
          </SheetTitle>
          <SheetDescription>
            {t('efs.masters.dismantling_methods.form_subtitle', { ns: NS })}
          </SheetDescription>
        </SheetHeader>

        {error ? (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        ) : null}

        <Form {...form}>
          <form onSubmit={onSubmit} className="mt-4 space-y-4" noValidate>
            <FormField
              control={form.control}
              name="method_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.dismantling_methods.fields.method_code', { ns: NS })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isEdit} placeholder="teardown" />
                  </FormControl>
                  <p className="text-xs text-efs-text-muted">
                    {isEdit
                      ? t('efs.masters.dismantling_methods.code_locked', { ns: NS })
                      : t('efs.masters.dismantling_methods.code_hint', { ns: NS })}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.dismantling_methods.fields.display_name', { ns: NS })}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="手動分解" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.masters.dismantling_methods.fields.description', { ns: NS })}
                  </FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <SheetFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                {t('efs.common.actions.cancel', { defaultValue: 'キャンセル' })}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.common.actions.save', { defaultValue: '保存' })}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
